/**
 * AssetIndex — quick "where everything lives" map for a generated content
 * package. Shows the admin every asset the content engine produced + a
 * direct link to view it (live URL / asset endpoint / tab anchor).
 *
 * Renders at the top of the ContentPackageEditor so admins don't have to
 * dig through tabs to know what was generated.
 *
 * Pure render — no API calls, no state.
 */
import {
    FileText,
    Image as ImageIcon,
    HelpCircle,
    ListChecks,
    Megaphone,
    Link as LinkIcon,
    Share2,
    Mail,
    ExternalLink,
    CheckCircle2,
    Circle,
    Sparkles,
    Eye,
} from "lucide-react";
import { useEffect, useState } from "react";
import {
    adminContentImageUrl,
    adminContentEngineChannels,
    adminListPackageImages,
} from "@/lib/api";
import { useNavigate } from "react-router-dom";

const PLATFORMS = [
    { key: "pinterest", label: "Pinterest", icon: ImageIcon },
    { key: "email", label: "Email newsletter", icon: Mail },
];

function Row({ icon: Icon, label, present, value, href, testid }) {
    const Indicator = present ? CheckCircle2 : Circle;
    return (
        <div
            className="flex items-start gap-3 py-2.5 border-b border-bl/30 last:border-0"
            data-testid={testid}
        >
            <Indicator
                className={
                    "h-4 w-4 mt-0.5 flex-shrink-0 " +
                    (present ? "text-[var(--bl-green)]" : "text-bl-muted/40")
                }
            />
            <div className="flex-shrink-0 w-44">
                <div className="text-xs font-medium text-bl flex items-center gap-1.5">
                    <Icon className="h-3 w-3 text-bl-muted" />
                    {label}
                </div>
            </div>
            <div className="flex-1 text-xs text-bl-muted min-w-0">
                {present ? (
                    <span className="text-bl">{value || "Generated"}</span>
                ) : (
                    <span className="italic">Not generated yet</span>
                )}
            </div>
            {href && present && (
                <a
                    href={href}
                    target="_blank"
                    rel="noreferrer"
                    className="bl-btn-ghost text-[11px] gap-1 flex-shrink-0"
                    data-testid={`${testid}-open`}
                >
                    Open <ExternalLink className="h-3 w-3" />
                </a>
            )}
        </div>
    );
}

export default function AssetIndex({ pkg, onJumpToTab }) {
    const navigate = useNavigate();
    // Channel-lock filter (v4.40.12) — locked platforms are hidden from
    // the Distribution assets summary so the admin doesn't see rows for
    // FB/IG/LinkedIn/X/legacy-video that will never auto-generate.
    const [activeChannels, setActiveChannels] = useState(null);
    // V1 image pipeline — track infographic asset presence so the row
    // resolves to a real public URL instead of the removed SVG endpoint.
    const [imageAssets, setImageAssets] = useState(null);
    useEffect(() => {
        let alive = true;
        adminContentEngineChannels()
            .then((r) => alive && r?.active && setActiveChannels(r.active))
            .catch(() => {});
        if (pkg?.package_id) {
            adminListPackageImages(pkg.package_id)
                .then((r) => alive && setImageAssets(r))
                .catch(() => {});
        }
        return () => { alive = false; };
    }, [pkg?.package_id]);
    const visibleDistPlatforms = activeChannels === null
        ? PLATFORMS
        : PLATFORMS.filter((p) => activeChannels.includes(p.key));

    if (!pkg) return null;

    const article = pkg.article_json || {};
    const seo = pkg.seo_metadata || {};
    const slug = pkg.published_slug || seo.slug;
    const liveUrl = slug ? `/learn/${slug}` : null;
    const sections = (article.article_sections || article.sections || []).length;
    const faqCount = (pkg.faq || []).length;
    const ctaCount = (pkg.cta_blocks || []).length;
    const linkCount = (pkg.internal_links?.links || pkg.internal_links || []).length || 0;
    const hero = pkg.hero_image || {};
    const heroUrl = hero.relative_path
        ? `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/api/learn/images/${pkg.package_id}/${(hero.relative_path || "").split("/").pop()}`
        : null;
    const socialAssets = pkg.social_assets || {};

    // Per-package AI cost estimate. The orchestrator records a
    // GenerationUsage row per LLM call in `ai_provider_metadata` with
    // {task, provider, model, char_in, char_out, success}. We don't have
    // exact billed tokens, but we can rough-estimate using published
    // per-token rates and chars/4 ≈ tokens.
    //   - claude-sonnet-4-5: $3.00/M input + $15.00/M output
    //   - gpt-4.1-mini:      $0.40/M input + $1.60/M output
    //   - gpt-4.1:           $2.00/M input + $8.00/M output
    //   - gemini-2.5-flash-image (nano banana): ~$0.0387/image
    // Fallback unknown model: $1/M in, $4/M out.
    const RATES = {
        "claude-sonnet-4-5-20250929": { in: 3.0, out: 15.0 },
        "gpt-4.1-mini": { in: 0.4, out: 1.6 },
        "gpt-4.1": { in: 2.0, out: 8.0 },
        "gpt-4o-mini": { in: 0.15, out: 0.6 },
    };
    const usage = pkg.ai_provider_metadata || [];
    let totalCostUsd = 0;
    let totalCalls = 0;
    let failedCalls = 0;
    let totalDurationMs = 0;
    for (const u of usage) {
        totalCalls += 1;
        if (u.success === false) failedCalls += 1;
        totalDurationMs += u.duration_ms || 0;
        const rate = RATES[u.model] || { in: 1.0, out: 4.0 };
        const tokensIn = (u.char_in || 0) / 4;
        const tokensOut = (u.char_out || 0) / 4;
        totalCostUsd += (tokensIn * rate.in + tokensOut * rate.out) / 1_000_000;
    }
    const costStr = totalCostUsd > 0 ? `$${totalCostUsd.toFixed(3)}` : null;

    return (
        <div className="bl-card p-5 space-y-3" data-testid="asset-index-panel">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-bl-primary" />
                    <h2 className="font-display font-semibold text-base text-bl tracking-tight">
                        Generated assets · what&rsquo;s in this package
                    </h2>
                </div>
                {pkg?.package_id && (
                    <button
                        type="button"
                        onClick={() =>
                            navigate(`/admin/content/${pkg.package_id}/article-preview`)
                        }
                        className="bl-btn-primary text-xs gap-1"
                        data-testid="asset-preview-link"
                    >
                        Preview article <Eye className="h-3 w-3" />
                    </button>
                )}
            </div>

            <p className="text-[11px] text-bl-muted leading-relaxed">
                Every asset below is stored in MongoDB (
                <code className="px-1 bg-bl-bg rounded text-[10px]">content_packages</code>
                {" / "}
                <code className="px-1 bg-bl-bg rounded text-[10px]">learn_articles</code>
                {" / "}
                <code className="px-1 bg-bl-bg rounded text-[10px]">email_newsletter_backlog</code>
                ). Image PNGs live on disk under{" "}
                <code className="px-1 bg-bl-bg rounded text-[10px]">/app/backend/.uploads/social/{pkg.package_id}/</code>.
            </p>

            {/* Per-package generation cost ledger — rough estimate based on
                char_in/char_out × published per-token rates. */}
            {totalCalls > 0 && (
                <div
                    className="rounded border border-bl/40 bg-bl-bg/40 px-3 py-2 flex flex-wrap items-center gap-x-5 gap-y-1 text-[11px]"
                    data-testid="asset-cost-ledger"
                >
                    <span className="text-bl-muted uppercase tracking-[0.16em] text-[10px]">
                        Generation cost · est.
                    </span>
                    {costStr && (
                        <span className="text-bl font-medium font-mono-bl" data-testid="asset-cost-total">
                            {costStr}
                        </span>
                    )}
                    <span className="text-bl-muted">
                        {totalCalls} call{totalCalls === 1 ? "" : "s"}
                    </span>
                    {failedCalls > 0 && (
                        <span className="text-[var(--bl-rust)]" data-testid="asset-cost-failed">
                            {failedCalls} failed
                        </span>
                    )}
                    {totalDurationMs > 0 && (
                        <span className="text-bl-muted">
                            {(totalDurationMs / 1000).toFixed(1)}s total
                        </span>
                    )}
                    <span className="text-bl-muted italic">
                        Estimate only — actual Universal Key spend may differ.
                    </span>
                </div>
            )}

            <div className="rounded border border-bl/40 divide-y divide-bl/30">
                <Row
                    icon={FileText}
                    label="Article body"
                    present={sections > 0}
                    value={`${sections} sections${article.read_time_min || article.estimated_read_minutes ? ` · ${article.read_time_min || article.estimated_read_minutes} min read` : ""}`}
                    href={liveUrl}
                    testid="asset-article"
                />
                <Row
                    icon={ListChecks}
                    label="SEO + Schema"
                    present={!!seo.slug}
                    value={seo.slug ? `slug: ${seo.slug}` : null}
                    testid="asset-seo"
                />
                <Row
                    icon={HelpCircle}
                    label="FAQ"
                    present={faqCount > 0}
                    value={`${faqCount} questions`}
                    testid="asset-faq"
                />
                <Row
                    icon={ImageIcon}
                    label="Hero image"
                    present={!!hero.relative_path}
                    value={hero.relative_path ? hero.relative_path.split("/").pop() : null}
                    href={heroUrl}
                    testid="asset-hero"
                />
                <Row
                    icon={ImageIcon}
                    label="Infographic"
                    present={
                        !!(imageAssets?.assets || []).find(
                            (a) => a.asset_type === "article_infographic"
                                && (a.generation_status === "generated" || a.generation_status === "approved"),
                        )
                    }
                    value={(() => {
                        const infoAsset = (imageAssets?.assets || []).find(
                            (a) => a.asset_type === "article_infographic",
                        );
                        if (!infoAsset) return null;
                        if (infoAsset.generation_status === "generated") return "Generated · awaiting approval";
                        if (infoAsset.generation_status === "approved") return "Approved";
                        if (infoAsset.generation_status === "failed") return `Failed: ${(infoAsset.error_message || "").slice(0, 60)}`;
                        return "Pending generation";
                    })()}
                    href={(() => {
                        const infoAsset = (imageAssets?.assets || []).find(
                            (a) => a.asset_type === "article_infographic"
                                && a.public_url,
                        );
                        if (!infoAsset?.public_url) return null;
                        return infoAsset.public_url.startsWith("http")
                            ? infoAsset.public_url
                            : `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}${infoAsset.public_url}`;
                    })()}
                    testid="asset-infographic"
                />
                <Row
                    icon={ListChecks}
                    label="Mini tool"
                    present={!!pkg.mini_tool_config}
                    value={pkg.mini_tool_config?.title || pkg.mini_tool_config?.tool_name}
                    testid="asset-mini-tool"
                />
                <Row
                    icon={Megaphone}
                    label="CTA blocks"
                    present={ctaCount > 0}
                    value={`${ctaCount} blocks`}
                    testid="asset-ctas"
                />
                <Row
                    icon={LinkIcon}
                    label="Internal links"
                    present={linkCount > 0}
                    value={`${linkCount} anchor suggestions`}
                    testid="asset-internal-links"
                />
            </div>

            <div className="text-[10px] uppercase tracking-[0.18em] text-bl-muted pt-2">
                Distribution assets
            </div>
            <div className="rounded border border-bl/40 divide-y divide-bl/30">
                {visibleDistPlatforms.map((p) => {
                    const entry = socialAssets[p.key];
                    const status = entry?.status;
                    let present = !!entry?.content || !!entry?.draft;
                    let value = status ? `status: ${status}` : null;
                    // Pinterest now ships 4 named pins (educational, curiosity,
                    // tool, video). Score it from the pin array, not the
                    // legacy single-image slot.
                    if (p.key === "pinterest") {
                        const pins = (entry?.pins || []).filter((x) => x && x.key);
                        const ready = pins.filter((pin) =>
                            pin.key === "video"
                                ? !!pin.video_relative_path
                                : !!pin.image_relative_path,
                        );
                        present = pins.length > 0;
                        value = pins.length
                            ? `${ready.length} / ${pins.length} pins ready`
                            : null;
                    }
                    const imgFile = p.key === "pinterest" && entry?.image_filename;
                    const href = imgFile
                        ? adminContentImageUrl(pkg.package_id, imgFile)
                        : null;
                    return (
                        <Row
                            key={p.key}
                            icon={p.icon}
                            label={p.label}
                            present={present}
                            value={value}
                            href={href}
                            testid={`asset-platform-${p.key}`}
                        />
                    );
                })}

                {/* YouTube assets removed in v4.44.2 — video generation
                    permanently locked. */}
            </div>

            <div className="text-[10px] text-bl-muted leading-relaxed pt-2">
                Open the <span className="font-medium text-bl">Distribution</span> tab to
                view, edit, or regenerate any platform asset. The{" "}
                <span className="font-medium text-bl">Preview</span> tab renders the
                live article exactly as it appears on{" "}
                <code className="text-[10px]">/learn/{slug || "..."}</code>.
            </div>
        </div>
    );
}
